import { useGeneratedSalesPortalAccountListQuery } from '@/experiences/sales-portal/blocs/documents/sales-portal-account-list.generated';
import { useMemo } from 'react';

/**
 * The mininmal shape for a sales hub account — a saved price quote, for example, remembers the chat id and name
 * but not the address or restaurant/supplier.
 */
export interface SalesHubAccount {
  chatId: string;
  currencyCode: string;
  name: string;
}

/**
 * A full account as it appears in the selector — the identity plus the address
 * and restaurant/supplier fields from the account-list query.
 */
export interface SalesHubAccountOption extends SalesHubAccount {
  address: string;
  restaurantUUID: string;
  supplierUUID: string;
}

interface UseAccountListLoaderResult {
  accounts: SalesHubAccountOption[];
  error: ReturnType<typeof useGeneratedSalesPortalAccountListQuery>['error'];
  loading: boolean;
  refetch: ReturnType<
    typeof useGeneratedSalesPortalAccountListQuery
  >['refetch'];
}

/**
 * Loads the sales rep's accessible accounts for the shared Sales Hub account
 * selector, shaped as `SalesHubAccountOption[]` sorted alphabetically by name.
 *
 * @param businessOrganizationUUID - Distributor-level org identifier; scopes
 *   the backend query to accounts this distributor serves.
 */
export const useAccountListLoader = (
  businessOrganizationUUID: string,
): UseAccountListLoaderResult => {
  const { data, loading, error, refetch } =
    useGeneratedSalesPortalAccountListQuery({
      fetchPolicy: 'cache-and-network',
      variables: { businessOrganizationUUID },
    });

  const accounts = useMemo<SalesHubAccountOption[]>(() => {
    if (!data) {
      return [];
    }
    return data.getSalesPortalAccounts
      .flatMap((account) =>
        account.restaurant
          ? [
              {
                address: account.restaurant.address.address,
                chatId: account.chat_id,
                currencyCode: account.currency_code,
                name: account.restaurant.name,
                restaurantUUID: account.restaurant_id,
                supplierUUID: account.supplier_id,
              },
            ]
          : [],
      )
      .sort((a, b) =>
        a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }),
      );
  }, [data]);

  return { accounts, error, loading, refetch };
};
